import { Box, Button, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import { tokens } from '../../../global/theme/tokens';
import MediaPreview from '../explorer/MediaPreview';
import { formatBytes } from '../explorer/MediaInfo';
import DateLabel from '../../label/DateLabel';
import {
  entryKind,
  entryLabel,
  folderPurposes,
  parentPath,
} from './explorerUtils';

const ExplorerInfoPane = ({ entry, preview, onDownload, onPreview }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const kind = entryKind(entry);
  const where = entry.path
    ? `${t('Project files')}/${parentPath(entry.path)}`.replace(/\/$/, '')
    : t('Project files');

  const purposes = folderPurposes(entry);

  const meta = [
    { label: t('Kind'), value: t(kind.label) },
    purposes.length > 0 && {
      label: t('Folder purposes'),
      value: purposes.map((purpose) => t(purpose.label)).join(', '),
    },
    !entry.isDir && {
      label: t('Size'),
      value: entry.size ? formatBytes(entry.size) : '—',
    },
    { label: t('Where'), value: where },
    !entry.isDir && {
      label: t('Modified'),
      value: entry.lastModified ? (
        <DateLabel date={entry.lastModified * 1000} />
      ) : (
        '—'
      ),
    },
    entry.meta?.workPackage && {
      label: t('Work Package'),
      value: entry.meta.workPackage.title,
    },
    entry.meta?.fileElement && {
      label: t('Progress'),
      value: `${entry.meta.fileElement.progress ?? 0}%`,
    },
  ].filter(Boolean);

  const hasFilePreview = !entry.isDir && preview?.path === entry.path;

  return (
    <Box
      className="flex-none w-72 h-full overflow-y-auto p-4 hidden lg:block"
      sx={{
        borderLeft: `1px solid ${colors.glass}`,
        backgroundColor: colors.paper,
      }}
    >
      <Box
        className="flex flex-col items-center gap-2 pb-4"
        sx={{ borderBottom: `1px solid ${colors.glass}` }}
      >
        {hasFilePreview ? (
          <MediaPreview
            allowPreview
            src={preview.url}
            mimeType={preview.mimeType || entry.mimeType || ''}
            size="92px"
            onPreviewClick={() => onPreview(entry)}
          />
        ) : (
          <Box
            className="flex items-center justify-center rounded-xl"
            sx={{
              width: '92px',
              height: '92px',
              backgroundColor: colors.bgInput,
              border: `1px solid ${colors.glass}`,
            }}
          >
            <kind.Icon sx={{ fontSize: '46px', color: kind.color }} />
          </Box>
        )}
        <Box className="text-center">
          <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
            {entryLabel(entry)}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            {t(kind.label)}
            {!entry.isDir && entry.size ? ` · ${formatBytes(entry.size)}` : ''}
          </Typography>
        </Box>
        {entry.path !== '' && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => onDownload(entry)}
          >
            {t('Download')}
          </Button>
        )}
      </Box>

      <Box className="flex flex-col mt-3">
        {meta.map((item) => (
          <Box
            key={item.label}
            className="flex flex-row items-baseline justify-between gap-3 py-2"
            sx={{ borderBottom: `1px solid ${colors.glass}` }}
          >
            <Typography variant="caption" sx={{ opacity: 0.6, flexShrink: 0 }}>
              {item.label}
            </Typography>
            <Typography
              variant="caption"
              className="text-right"
              sx={{ wordBreak: 'break-word' }}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ExplorerInfoPane;
